import { useRef, useState, useMemo } from 'react';
import {
  Check,
  AlertTriangle,
  Download,
  Upload,
  FileCode,
  FileText,
  Server,
  Network,
  Shield,
  Package,
  Zap,
  DollarSign,
  Terminal,
  BookOpen,
  Workflow,
  Lock,
  Container,
  Image,
  Share2,
  HardDrive,
  Cpu,
  Activity,
  Printer,
  ArrowRight,
  Key,
  Settings,
  ArrowLeftRight,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, Badge, NodeTypeBadge, Button, useToast, CopyableIP, CopyableHostname } from '../../ui';
import { ResourceChart } from '../../charts';
import { useConfigurator } from '../../../hooks/useConfigurator';
import { DatacenterConfig, WizardStep } from '../../../types/configurator';
import { SCENARIO_PRESETS } from '../../../data/scenarios';
import { getServiceById } from '../../../data/services';
import {
  generatePlantUML,
  generateMermaidDiagram,
  generateBootstrapScript,
  generateMarkdownDoc,
  generateWireGuardConfig,
  generateDockerCompose,
  generateNetworkDiagramSVG,
} from '../../../services/exporters';
import { ComparePanel } from '../../comparison';
import { PowerCoolingSummary } from '../../power';
import { CapacityPlanningPanel } from '../../planning';

export function SummaryStep() {
  const { config, validate, loadConfig, goToStep, addNode } = useConfigurator();
  const { success, error, info } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showCompare, setShowCompare] = useState(false);

  // Validate on render
  const validationErrors = validate();
  const hasErrors = validationErrors.filter((e) => e.severity === 'error').length > 0;
  const hasWarnings = validationErrors.filter((e) => e.severity === 'warning').length > 0;

  const scenario = SCENARIO_PRESETS.find((s) => s.id === config.scenario);

  // Node type counts
  const nodeCounts = {
    gateway: config.nodes.filter((n) => n.type === 'gateway').length,
    compute: config.nodes.filter((n) => n.type === 'compute').length,
    storage: config.nodes.filter((n) => n.type === 'storage').length,
    daq: config.nodes.filter((n) => n.type === 'daq').length,
  };

  // Total specs
  const totalSpecs = config.nodes.reduce(
    (acc, node) => ({
      cpu: acc.cpu + node.specs.cpuCores,
      memory: acc.memory + node.specs.memoryGB,
      storage: acc.storage + node.specs.storageGB,
    }),
    { cpu: 0, memory: 0, storage: 0 }
  );

  const handleImport = () => {
    fileInputRef.current?.click();
  };

  const handleShare = async () => {
    try {
      // Create a minimal config for sharing
      const shareData = JSON.stringify(config);
      const encoded = btoa(shareData);
      const url = `${window.location.origin}${window.location.pathname}?config=${encoded}`;

      // Try to use clipboard
      await navigator.clipboard.writeText(url);
      success('Share link copied to clipboard!');
    } catch {
      error('Failed to create share link');
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const imported = JSON.parse(content) as DatacenterConfig;

        // Basic validation
        if (!imported.scenario || !imported.network || !imported.nodes) {
          error('Invalid configuration file: missing required fields');
          return;
        }

        loadConfig(imported);
        success(`Configuration "${imported.name}" loaded successfully`);
      } catch {
        error('Failed to parse configuration file. Ensure it is valid JSON.');
      }
    };
    reader.readAsText(file);

    // Reset input
    event.target.value = '';
  };

  return (
    <div className="space-y-6">
      {/* Print Header - only visible when printing */}
      <div className="print-only print-header">
        <h1 className="text-2xl font-bold">hermit-μDC Configuration</h1>
        <p className="text-lg">{config.name}</p>
        <p className="text-sm text-gray-600">
          Generated: {new Date().toLocaleDateString()} | Scenario: {scenario?.name}
        </p>
      </div>

      <div className="flex items-start justify-between no-print">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Configuration Summary</h2>
          <p className="mt-1 text-gray-500">
            Review your configuration and export deployment artifacts.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowCompare(true)} className="gap-2">
            <ArrowLeftRight className="h-4 w-4" />
            Compare
          </Button>
          <Button variant="outline" onClick={() => window.print()} className="gap-2">
            <Printer className="h-4 w-4" />
            Print
          </Button>
          <Button variant="outline" onClick={handleShare} className="gap-2">
            <Share2 className="h-4 w-4" />
            Share
          </Button>
        </div>
      </div>

      {/* Quick Stats Bar */}
      <div className="grid grid-cols-2 gap-4 rounded-lg border border-gray-200 bg-white p-4 sm:grid-cols-4 lg:grid-cols-7">
        <div className="flex items-center gap-2">
          <Server className="h-5 w-5 text-orange-500" />
          <div>
            <div className="text-lg font-bold text-gray-900">{nodeCounts.gateway}</div>
            <div className="text-xs text-gray-500">Gateway</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Cpu className="h-5 w-5 text-blue-500" />
          <div>
            <div className="text-lg font-bold text-gray-900">{nodeCounts.compute}</div>
            <div className="text-xs text-gray-500">Compute</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <HardDrive className="h-5 w-5 text-green-500" />
          <div>
            <div className="text-lg font-bold text-gray-900">{nodeCounts.storage}</div>
            <div className="text-xs text-gray-500">Storage</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-purple-500" />
          <div>
            <div className="text-lg font-bold text-gray-900">{nodeCounts.daq}</div>
            <div className="text-xs text-gray-500">DAQ</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Cpu className="h-5 w-5 text-gray-400" />
          <div>
            <div className="text-lg font-bold text-gray-900">{totalSpecs.cpu}</div>
            <div className="text-xs text-gray-500">CPU Cores</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Package className="h-5 w-5 text-gray-400" />
          <div>
            <div className="text-lg font-bold text-gray-900">{totalSpecs.memory}GB</div>
            <div className="text-xs text-gray-500">Memory</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <HardDrive className="h-5 w-5 text-gray-400" />
          <div>
            <div className="text-lg font-bold text-gray-900">
              {totalSpecs.storage >= 1000
                ? `${(totalSpecs.storage / 1000).toFixed(1)}TB`
                : `${totalSpecs.storage}GB`}
            </div>
            <div className="text-xs text-gray-500">Storage</div>
          </div>
        </div>
      </div>

      {/* Validation Status */}
      {(hasErrors || hasWarnings) && (
        <Card className={hasErrors ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'}>
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertTriangle
                className={`h-5 w-5 ${hasErrors ? 'text-red-500' : 'text-amber-500'}`}
              />
              <div>
                <h3
                  className={`font-medium ${hasErrors ? 'text-red-800' : 'text-amber-800'}`}
                >
                  {hasErrors ? 'Configuration Errors' : 'Configuration Warnings'}
                </h3>
                <ul className="mt-2 space-y-1">
                  {validationErrors.map((error, i) => (
                    <li
                      key={i}
                      className={`text-sm ${
                        error.severity === 'error' ? 'text-red-700' : 'text-amber-700'
                      }`}
                    >
                      {error.message}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {!hasErrors && !hasWarnings && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Check className="h-5 w-5 text-green-500" />
              <span className="font-medium text-green-800">
                Configuration is valid and ready for export
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions - Hidden when printing */}
      <QuickActions
        config={config}
        onGoToStep={goToStep}
        onAddNode={(type) => {
          addNode(type);
          info(`Added ${type} node`);
        }}
      />

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Overview */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Server className="h-5 w-5 text-hermit-600" />
              <CardTitle>Overview</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-gray-500">Configuration Name</dt>
                <dd className="font-medium text-gray-900">{config.name}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Scenario</dt>
                <dd className="font-medium text-gray-900">{scenario?.name}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Total Nodes</dt>
                <dd className="font-medium text-gray-900">{config.nodes.length}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="flex items-center gap-1 text-gray-500">
                  <Zap className="h-4 w-4" /> Power
                </dt>
                <dd className="font-medium text-gray-900">{config.estimatedPower}W</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="flex items-center gap-1 text-gray-500">
                  <DollarSign className="h-4 w-4" /> Est. Cost
                </dt>
                <dd className="font-medium text-gray-900">
                  ${config.estimatedCost.toFixed(0)}
                </dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        {/* Network Summary */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Network className="h-5 w-5 text-hermit-600" />
              <CardTitle>Network</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-gray-500">Subnet</dt>
                <dd className="font-mono text-gray-900">{config.network.subnet}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Gateway</dt>
                <dd className="font-mono text-gray-900">{config.network.gateway}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Domain</dt>
                <dd className="font-mono text-gray-900">{config.network.domain}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Internet Access</dt>
                <dd className="font-medium text-red-600">Blocked (Airgap)</dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        {/* Nodes */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Server className="h-5 w-5 text-hermit-600" />
              <CardTitle>Nodes ({config.nodes.length})</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {config.nodes.map((node) => (
                <div
                  key={node.id}
                  className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2"
                >
                  <div className="flex items-center gap-2">
                    <NodeTypeBadge type={node.type} />
                    <CopyableHostname
                      hostname={node.hostname}
                      domain={config.network.domain}
                      className="text-gray-900"
                    />
                  </div>
                  <CopyableIP ip={node.ip} className="text-gray-500" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Services */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-hermit-600" />
              <CardTitle>Services ({config.serviceAllocations.length})</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {config.serviceAllocations.map((alloc) => {
                const service = getServiceById(alloc.serviceId);
                const node = config.nodes.find((n) => n.id === alloc.nodeId);
                if (!service || !node) return null;

                return (
                  <div
                    key={`${alloc.serviceId}-${alloc.nodeId}`}
                    className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{service.name}</span>
                      {service.critical && (
                        <Badge variant="error" size="sm">
                          Critical
                        </Badge>
                      )}
                    </div>
                    <span className="text-sm text-gray-500">{node.hostname}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Security */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-hermit-600" />
              <CardTitle>Security</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <dt className="text-sm text-gray-500">VPN</dt>
                <dd className="mt-1 flex items-center gap-2">
                  <Badge variant={config.security.vpnEnabled ? 'success' : 'default'}>
                    {config.security.vpnEnabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                  {config.security.vpnEnabled && (
                    <span className="text-sm text-gray-600">
                      :{config.security.vpnListenPort}
                    </span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Authorized Peers</dt>
                <dd className="mt-1">
                  <Badge variant={config.security.peers.length > 0 ? 'success' : 'warning'}>
                    {config.security.peers.filter((p) => p.enabled).length} active
                  </Badge>
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Encryption</dt>
                <dd className="mt-1">
                  <Badge variant={config.security.encryptStorage ? 'success' : 'default'}>
                    {config.security.encryptStorage ? 'LUKS Enabled' : 'Unencrypted'}
                  </Badge>
                </dd>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Resource Breakdown */}
      {config.nodes.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-hermit-600" />
              <CardTitle>Resource Breakdown</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <ResourceChart config={config} />
          </CardContent>
        </Card>
      )}

      {/* Power & Cooling Analysis */}
      {config.nodes.length > 0 && <PowerCoolingSummary config={config} />}

      {/* Capacity Planning */}
      {config.nodes.length > 0 && <CapacityPlanningPanel config={config} />}

      {/* Import/Export Options - Hidden when printing */}
      <Card className="no-print">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Download className="h-5 w-5 text-hermit-600" />
              <CardTitle>Import / Export Configuration</CardTitle>
            </div>
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleFileChange}
                className="hidden"
              />
              <Button variant="outline" onClick={handleImport} className="gap-2">
                <Upload className="h-4 w-4" />
                Load Config
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="mb-3 text-sm text-gray-500">Configuration & Data</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <ExportButton
              icon={FileText}
              label="JSON Config"
              description="Full configuration export"
              format="json"
              config={config}
            />
            <ExportButton
              icon={FileCode}
              label="SysML Model"
              description="SysML 2.0 system model"
              format="sysml"
              config={config}
            />
            <ExportButton
              icon={FileText}
              label="Ansible Inventory"
              description="hosts.yml for provisioning"
              format="ansible"
              config={config}
            />
            <ExportButton
              icon={FileText}
              label="Bill of Materials"
              description="Parts list with costs"
              format="bom"
              config={config}
            />
          </div>

          <p className="mb-3 mt-6 text-sm text-gray-500">Diagrams & Documentation</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <ExportButton
              icon={Image}
              label="SVG Diagram"
              description="Network topology image"
              format="svg"
              config={config}
            />
            <ExportButton
              icon={Workflow}
              label="PlantUML"
              description="Deployment diagram"
              format="plantuml"
              config={config}
            />
            <ExportButton
              icon={FileCode}
              label="Mermaid"
              description="Markdown diagram"
              format="mermaid"
              config={config}
            />
            <ExportButton
              icon={BookOpen}
              label="Markdown Doc"
              description="Full documentation"
              format="markdown"
              config={config}
            />
          </div>

          <p className="mb-3 mt-6 text-sm text-gray-500">Infrastructure & Security</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <ExportButton
              icon={Lock}
              label="WireGuard"
              description="VPN server config"
              format="wireguard"
              config={config}
            />
            <ExportButton
              icon={Container}
              label="Docker Compose"
              description="Service containers"
              format="docker"
              config={config}
            />
            <ExportButton
              icon={Terminal}
              label="Bootstrap Script"
              description="Node setup shell script"
              format="bootstrap"
              config={config}
            />
          </div>
        </CardContent>
      </Card>

      {/* Compare Panel Modal */}
      <ComparePanel isOpen={showCompare} onClose={() => setShowCompare(false)} />
    </div>
  );
}

interface ExportButtonProps {
  icon: typeof FileCode;
  label: string;
  description: string;
  format: string;
  config: DatacenterConfig;
}

function ExportButton({ icon: Icon, label, description, format, config }: ExportButtonProps) {
  const { success } = useToast();

  const handleExport = () => {
    let content: string;
    let filename: string;
    let mimeType: string;

    switch (format) {
      case 'json':
        content = JSON.stringify(config, null, 2);
        filename = `hermit-config-${Date.now()}.json`;
        mimeType = 'application/json';
        break;
      case 'sysml':
        content = generateSysML(config);
        filename = `HermitConfig.sysml`;
        mimeType = 'text/plain';
        break;
      case 'ansible':
        content = generateAnsibleInventory(config);
        filename = `hosts.yml`;
        mimeType = 'text/yaml';
        break;
      case 'bom':
        content = generateBOM(config);
        filename = `hermit-bom.csv`;
        mimeType = 'text/csv';
        break;
      case 'plantuml':
        content = generatePlantUML(config);
        filename = `hermit-diagram.puml`;
        mimeType = 'text/plain';
        break;
      case 'mermaid':
        content = generateMermaidDiagram(config);
        filename = `hermit-diagram.md`;
        mimeType = 'text/markdown';
        break;
      case 'markdown':
        content = generateMarkdownDoc(config);
        filename = `hermit-documentation.md`;
        mimeType = 'text/markdown';
        break;
      case 'bootstrap':
        content = generateBootstrapScript(config);
        filename = `bootstrap.sh`;
        mimeType = 'text/x-shellscript';
        break;
      case 'wireguard':
        content = generateWireGuardConfig(config);
        filename = `wg0.conf`;
        mimeType = 'text/plain';
        break;
      case 'docker':
        content = generateDockerCompose(config);
        filename = `docker-compose.yml`;
        mimeType = 'text/yaml';
        break;
      case 'svg':
        content = generateNetworkDiagramSVG(config);
        filename = `hermit-topology.svg`;
        mimeType = 'image/svg+xml';
        break;
      default:
        return;
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    success(`Exported ${label} as ${filename}`);
  };

  return (
    <button
      onClick={handleExport}
      className="flex flex-col items-center rounded-lg border border-gray-200 p-4 text-center transition-colors hover:border-hermit-300 hover:bg-hermit-50"
    >
      <Icon className="h-8 w-8 text-hermit-600" />
      <span className="mt-2 font-medium text-gray-900">{label}</span>
      <span className="text-xs text-gray-500">{description}</span>
    </button>
  );
}

// Simple SysML generator (will be enhanced later)
function generateSysML(config: DatacenterConfig): string {
  const lines: string[] = [
    '/**',
    ` * hermit-μDC Configuration: ${config.name}`,
    ` * Generated: ${new Date().toISOString()}`,
    ' */',
    `package HermitConfig {`,
    `    import Hermit::*;`,
    '',
    `    // Configuration metadata`,
    `    attribute configName : String = "${config.name}";`,
    `    attribute scenario : String = "${config.scenario}";`,
    `    attribute generatedAt : String = "${new Date().toISOString()}";`,
    '',
    `    // Network configuration`,
    `    part network : HermitNetwork {`,
    `        attribute subnet : String = "${config.network.subnet}";`,
    `        attribute gateway : String = "${config.network.gateway}";`,
    `        attribute domain : String = "${config.network.domain}";`,
    `    }`,
    '',
    `    // Node definitions`,
  ];

  for (const node of config.nodes) {
    const typeName = node.type.charAt(0).toUpperCase() + node.type.slice(1);
    lines.push(`    part ${node.hostname} : ${typeName}Node {`);
    lines.push(`        :>> hostname = "${node.hostname}";`);
    lines.push(`        :>> ipAddress = "${node.ip}";`);
    lines.push(`        attribute cpuCores : Integer = ${node.specs.cpuCores};`);
    lines.push(`        attribute memoryGB : Integer = ${node.specs.memoryGB};`);
    lines.push(`        attribute storageGB : Integer = ${node.specs.storageGB};`);
    lines.push(`        attribute powerWatts : Integer = ${node.specs.powerWatts};`);
    if (node.role) {
      lines.push(`        attribute role : String = "${node.role}";`);
    }
    lines.push(`    }`);
    lines.push('');
  }

  lines.push(`    // Security configuration`);
  lines.push(`    part security : SecurityConfig {`);
  lines.push(`        attribute vpnEnabled : Boolean = ${config.security.vpnEnabled};`);
  lines.push(`        attribute vpnPort : Integer = ${config.security.vpnListenPort};`);
  lines.push(`        attribute encryptStorage : Boolean = ${config.security.encryptStorage};`);
  lines.push(`        attribute auditLogging : Boolean = ${config.security.auditLogging};`);
  lines.push(`    }`);
  lines.push('');
  lines.push(`    // Computed totals`);
  lines.push(`    attribute totalNodes : Integer = ${config.nodes.length};`);
  lines.push(`    attribute totalPower : Integer = ${config.estimatedPower};`);
  lines.push(`    attribute estimatedCost : Integer = ${Math.round(config.estimatedCost)};`);
  lines.push('}');

  return lines.join('\n');
}

function generateAnsibleInventory(config: DatacenterConfig): string {
  const lines: string[] = [
    '# Ansible inventory for hermit-μDC',
    `# Generated: ${new Date().toISOString()}`,
    '',
    'all:',
    '  vars:',
    `    domain: ${config.network.domain}`,
    `    subnet: ${config.network.subnet}`,
    `    gateway: ${config.network.gateway}`,
    '',
    '  children:',
  ];

  const nodesByType: Record<string, typeof config.nodes> = {};
  for (const node of config.nodes) {
    if (!nodesByType[node.type]) {
      nodesByType[node.type] = [];
    }
    nodesByType[node.type].push(node);
  }

  for (const [type, nodes] of Object.entries(nodesByType)) {
    lines.push(`    ${type}:`);
    lines.push('      hosts:');
    for (const node of nodes) {
      lines.push(`        ${node.hostname}:`);
      lines.push(`          ansible_host: ${node.ip}`);
      if (node.role) {
        lines.push(`          role: ${node.role}`);
      }
    }
  }

  return lines.join('\n');
}

function generateBOM(config: DatacenterConfig): string {
  const lines: string[] = [
    'Qty,Type,Hostname,Model,CPU Cores,Memory (GB),Storage (GB),Power (W),Est. Cost',
  ];

  for (const node of config.nodes) {
    const cost =
      (node.type === 'gateway' ? 50 : node.type === 'storage' ? 200 : 100) +
      node.specs.cpuCores * 20 +
      node.specs.memoryGB * 5 +
      node.specs.storageGB * 0.5;

    lines.push(
      [
        1,
        node.type,
        node.hostname,
        node.specs.model || 'Generic',
        node.specs.cpuCores,
        node.specs.memoryGB,
        node.specs.storageGB,
        node.specs.powerWatts,
        cost.toFixed(2),
      ].join(',')
    );
  }

  lines.push('');
  lines.push(`,,,,,,Total:,${config.estimatedPower}W,$${config.estimatedCost.toFixed(2)}`);

  return lines.join('\n');
}

// Quick Actions Component
interface QuickActionsProps {
  config: DatacenterConfig;
  onGoToStep: (step: WizardStep) => void;
  onAddNode: (type: 'gateway' | 'compute' | 'storage' | 'daq') => void;
}

function QuickActions({ config, onGoToStep, onAddNode }: QuickActionsProps) {
  const actions = useMemo(() => {
    const result: Array<{
      id: string;
      icon: typeof Server;
      label: string;
      description: string;
      variant: 'default' | 'primary' | 'warning';
      onClick: () => void;
    }> = [];

    // Check for missing gateway
    if (!config.nodes.some((n) => n.type === 'gateway')) {
      result.push({
        id: 'add-gateway',
        icon: Server,
        label: 'Add Gateway',
        description: 'Required for network to function',
        variant: 'warning',
        onClick: () => onAddNode('gateway'),
      });
    }

    // Suggest adding compute if none exists
    if (!config.nodes.some((n) => n.type === 'compute')) {
      result.push({
        id: 'add-compute',
        icon: Cpu,
        label: 'Add Compute Node',
        description: 'Run workloads and containers',
        variant: 'default',
        onClick: () => onAddNode('compute'),
      });
    }

    // Check VPN configuration
    if (config.security.vpnEnabled && config.security.peers.length === 0) {
      result.push({
        id: 'configure-vpn',
        icon: Key,
        label: 'Configure VPN Peers',
        description: 'Add authorized operators',
        variant: 'warning',
        onClick: () => onGoToStep('security'),
      });
    }

    // Check for missing VPN peers public keys
    const peersWithoutKeys = config.security.peers.filter((p) => p.enabled && !p.publicKey);
    if (peersWithoutKeys.length > 0) {
      result.push({
        id: 'add-vpn-keys',
        icon: Lock,
        label: 'Add VPN Public Keys',
        description: `${peersWithoutKeys.length} peer(s) need keys`,
        variant: 'warning',
        onClick: () => onGoToStep('security'),
      });
    }

    // Suggest storage if not present and has compute
    if (config.nodes.some((n) => n.type === 'compute') && !config.nodes.some((n) => n.type === 'storage')) {
      result.push({
        id: 'add-storage',
        icon: HardDrive,
        label: 'Add Storage Node',
        description: 'Persistent data storage',
        variant: 'default',
        onClick: () => onAddNode('storage'),
      });
    }

    // Quick navigation actions (if no critical actions)
    if (result.length < 3) {
      result.push({
        id: 'edit-nodes',
        icon: Settings,
        label: 'Edit Nodes',
        description: 'Modify node configuration',
        variant: 'default',
        onClick: () => onGoToStep('nodes'),
      });

      result.push({
        id: 'edit-services',
        icon: Package,
        label: 'Edit Services',
        description: 'Manage service allocations',
        variant: 'default',
        onClick: () => onGoToStep('services'),
      });

      result.push({
        id: 'edit-security',
        icon: Shield,
        label: 'Edit Security',
        description: 'Configure VPN and firewall',
        variant: 'default',
        onClick: () => onGoToStep('security'),
      });
    }

    return result.slice(0, 4); // Max 4 actions
  }, [config, onGoToStep, onAddNode]);

  if (actions.length === 0) return null;

  const hasWarnings = actions.some((a) => a.variant === 'warning');

  return (
    <Card className={`no-print ${hasWarnings ? 'border-amber-200' : 'border-gray-200'}`}>
      <CardHeader className="py-3">
        <div className="flex items-center gap-2">
          <Zap className={`h-4 w-4 ${hasWarnings ? 'text-amber-500' : 'text-hermit-600'}`} />
          <CardTitle className="text-sm">
            {hasWarnings ? 'Suggested Actions' : 'Quick Actions'}
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {actions.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.id}
                onClick={action.onClick}
                className={`flex items-center gap-3 rounded-lg border p-3 text-left transition-colors ${
                  action.variant === 'warning'
                    ? 'border-amber-200 bg-amber-50 hover:bg-amber-100'
                    : 'border-gray-200 bg-white hover:bg-gray-50'
                }`}
              >
                <div
                  className={`rounded-lg p-2 ${
                    action.variant === 'warning'
                      ? 'bg-amber-100 text-amber-600'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 text-sm truncate">{action.label}</div>
                  <div className="text-xs text-gray-500 truncate">{action.description}</div>
                </div>
                <ArrowRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
